import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Box,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Grid,
    TablePagination,
    Tooltip,
    TableSortLabel,
    Card,
    CardContent,
    CardActions,
    Avatar,
    Switch,
    useTheme,
    useMediaQuery,
    styled,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Print as PrintIcon,
    GetApp as GetAppIcon,
} from '@mui/icons-material';
import { collection, getDocs, deleteDoc, doc, query, orderBy, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { PedidoPreprogramado, Route } from '../models';

interface PreprogrammedOrder extends Omit<PedidoPreprogramado, 'id'> {
    id: string;
    originRouteName?: string;
    destinyRouteName?: string;
    clientName?: string;
}

const PreprogrammedOrders: React.FC = () => {
    const [orders, setOrders] = useState<PreprogrammedOrder[]>([]);
    const [filteredOrders, setFilteredOrders] = useState<PreprogrammedOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    const handlePrint = () => {
        window.print();
    };

    const handleDownloadCSV = () => {
        // Create CSV header
        const headers = ['Cliente', 'Fecha Origen', 'Ruta Origen', 'Fecha Destino', 'Ruta Destino'];
        
        // Create CSV rows
        const csvRows = [
            headers.join(','),
            ...filteredOrders.map(order => [
                `"${order.clientName || ''}"`,
                `"${order.originReprogrammedDate ? format(order.originReprogrammedDate, 'PPP', { locale: es }) : 'N/A'}"`,
                `"${order.originRouteName || ''}"`,
                `"${order.destinyReprogrammedDate ? format(order.destinyReprogrammedDate, 'PPP', { locale: es }) : 'N/A'}"`,
                `"${order.destinyRouteName || ''}"`
            ].join(','))
        ];
        
        // Create CSV content
        const csvContent = csvRows.join('\n');
        
        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `pedidos_preprogramados_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Function to get client name by ID
    const getClientName = async (clientId: string): Promise<string> => {
        try {
            const clientDoc = await getDoc(doc(db, 'clients', clientId));
            if (clientDoc.exists()) {
                return clientDoc.data().name || 'Cliente no encontrado';
            }
            return 'Cliente no encontrado';
        } catch (error) {
            console.error('Error fetching client:', error);
            return 'Error al cargar';
        }
    };

    // Function to get route name by ID
    const getRouteName = async (routeId: string): Promise<string> => {
        try {
            const routeDoc = await getDoc(doc(db, 'routeGroups', routeId));
            if (routeDoc.exists()) {
                return routeDoc.data().name || 'Ruta sin nombre';
            }
            return 'Ruta no encontrada';
        } catch (error) {
            console.error('Error fetching route:', error);
            return 'Error al cargar';
        }
    };

    useEffect(() => {
        fetchPreprogrammedOrders();
    }, []);

    const fetchPreprogrammedOrders = async () => {
        try {
            setLoading(true);
            const ordersRef = collection(db, 'pedidoPreprogramado');
            const q = query(ordersRef, orderBy('destinyReprogrammedDate', 'asc'));
            const querySnapshot = await getDocs(q);

            const ordersData = await Promise.all(querySnapshot.docs.map(async (doc) => {
                const data = doc.data();
                const originRouteName = await getRouteName(data.originReprogrammedRouteGroupId);
                const destinyRouteName = await getRouteName(data.destinyReprogrammedRouteGroupId);
                let clientName = 'No asignado';

                if (data.routeRreprogrammed?.clientId) {
                    clientName = await getClientName(data.routeRreprogrammed.clientId);
                }

                return {
                    id: doc.id,
                    originReprogrammedRouteGroupId: data.originReprogrammedRouteGroupId,
                    destinyReprogrammedRouteGroupId: data.destinyReprogrammedRouteGroupId,
                    originReprogrammedDate: data.originReprogrammedDate?.toDate(),
                    destinyReprogrammedDate: data.destinyReprogrammedDate?.toDate(),
                    routeRreprogrammed: data.routeRreprogrammed,
                    originRouteName,
                    destinyRouteName,
                    clientName
                } as PreprogrammedOrder;
            }));

            setOrders(ordersData);
            setFilteredOrders(ordersData);
        } catch (error) {
            console.error('Error fetching preprogrammed orders:', error);
            toast.error('Error al cargar los pedidos preprogramados');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (orderId: string) => {
        setOrderToDelete(orderId);
        setDeleteDialogOpen(true);
    };

    // Helper function to normalize strings by removing accents
    const normalizeString = (str: string): string => {
        return str
            .normalize('NFD') // Normalize to NFD Unicode normalization form (separates base characters from diacritics)
            .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
            .toLowerCase();
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);
        
        if (value === '') {
            setFilteredOrders(orders);
        } else {
            const normalizedSearch = normalizeString(value);
            
            const filtered = orders.filter(order => {
                // Normalize all strings for comparison
                const clientName = order.clientName ? normalizeString(order.clientName) : '';
                const originRoute = order.originRouteName ? normalizeString(order.originRouteName) : '';
                const destinyRoute = order.destinyRouteName ? normalizeString(order.destinyRouteName) : '';
                const originDate = order.originReprogrammedDate ? 
                    normalizeString(format(order.originReprogrammedDate, 'PPP', { locale: es })) : '';
                const destinyDate = order.destinyReprogrammedDate ? 
                    normalizeString(format(order.destinyReprogrammedDate, 'PPP', { locale: es })) : '';
                
                return (
                    clientName.includes(normalizedSearch) ||
                    originRoute.includes(normalizedSearch) ||
                    destinyRoute.includes(normalizedSearch) ||
                    originDate.includes(normalizedSearch) ||
                    destinyDate.includes(normalizedSearch)
                );
            });
            
            setFilteredOrders(filtered);
        }
        setPage(0); // Reset to first page when searching
    };

    const handleDeleteConfirm = async () => {
        if (!orderToDelete) return;

        try {
            await deleteDoc(doc(db, 'pedidoPreprogramado', orderToDelete));
            const updatedOrders = orders.filter(order => order.id !== orderToDelete);
            setOrders(updatedOrders);
            setFilteredOrders(updatedOrders);
            toast.success('Pedido preprogramado eliminado exitosamente');
        } catch (error) {
            console.error('Error deleting preprogrammed order:', error);
            toast.error('Error al eliminar el pedido preprogramado');
        } finally {
            setDeleteDialogOpen(false);
            setOrderToDelete(null);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'success.main';
            case 'paused':
                return 'warning.main';
            case 'completed':
                return 'text.secondary';
            default:
                return 'text.primary';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'active':
                return 'Activo';
            case 'paused':
                return 'Pausado';
            case 'completed':
                return 'Completado';
            default:
                return status;
        }
    };

    // Remove getFrequencyText as it's no longer needed

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                color: 'rgb(0, 180, 216)'
            }}>
                <h1>
                    Pedidos Preprogramados
                </h1>
            </Box>
            {/* linea horizontal de lado a lado con un ancho de 4px que sea de color rgb(0, 180, 216) */}
            <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'rgb(0, 180, 216)',
                margin: '20px 0'
            }} />
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
                        }}
                        sx={{ width: 300 }}
                    />
                    <Tooltip title="Imprimir">
                        <IconButton onClick={handlePrint}>
                            <PrintIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Descargar CSV">
                        <IconButton onClick={handleDownloadCSV}>
                            <GetAppIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, minHeight: 'calc(60vh)' }}>
                <Table sx={{ minWidth: 650 }} aria-label="tabla de pedidos preprogramados">
                    <TableHead sx={{ backgroundColor: 'rgb(0, 180, 216)' }}>
                        <TableRow>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cliente</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha Origen</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ruta Origen</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha Destino</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ruta Destino</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ minHeight: '400px' }}>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ height: '470px', border: 'none' }}>
                                    <Box sx={{
                                        display: 'flex',
                                        height: '100%',
                                        width: '100%',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <CircularProgress size={60} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : filteredOrders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        No se encontraron resultados para la búsqueda
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredOrders
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((order) => (
                                    <TableRow
                                        key={order.id}
                                        hover
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 180, 216, 0.1)',
                                                '& .action-icons': {
                                                    opacity: 1
                                                }
                                            }
                                        }}
                                    >
                                        <TableCell>{order.clientName || 'No asignado'}</TableCell>
                                        <TableCell>
                                            {order.originReprogrammedDate ? format(order.originReprogrammedDate, 'PPP', { locale: es }) : 'N/A'}
                                        </TableCell>
                                        <TableCell>{order.originRouteName || 'Cargando...'}</TableCell>
                                        <TableCell>
                                            {order.destinyReprogrammedDate ? format(order.destinyReprogrammedDate, 'PPP', { locale: es }) : 'N/A'}
                                        </TableCell>
                                        <TableCell>{order.destinyRouteName || 'Cargando...'}</TableCell>
                                        <TableCell align="right">
                                            <Box
                                                className="action-icons"
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    justifyContent: 'flex-end',
                                                    opacity: { xs: 1, md: 0.7 },
                                                    '&:hover': {
                                                        opacity: 1
                                                    }
                                                }}
                                            >
                                                <Tooltip title="Eliminar">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteClick(order.id)}
                                                        sx={{
                                                            color: 'error.main',
                                                            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' }
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[7, 11, 25]}
                component="div"
                count={filteredOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        margin: 0,
                    },
                    '.MuiTablePagination-toolbar': {
                        paddingLeft: 2,
                        paddingRight: 2,
                    },
                    mt: 1,
                    borderTop: '1px solid rgba(224, 224, 224, 0.5)'
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <Typography>¿Estás seguro de que deseas eliminar este pedido preprogramado?</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PreprogrammedOrders;
